/**
 * Set your password — the destination of the welcome email link.
 *
 * The shape of this screen is dictated by two facts about the API:
 *
 *   - **The link carries a single-use token that lives 24 hours.** So the page
 *     verifies it up front (`GET /auth/set-password?token=…`) rather than
 *     letting someone fill in a form that cannot possibly work, and offers a
 *     way to get a fresh link when it has lapsed.
 *   - **Redeeming the token returns no session** — `{ success, email }` and
 *     nothing else, by design. So this screen signs the user in immediately
 *     afterwards using the password they just chose, which is the only moment
 *     the app legitimately holds it. It is never stored, only passed straight
 *     to `POST /auth/login`.
 *
 * The API builds links as `${APP_BASE_URL}/set-password?token=…`. The path form
 * `/set-password/:token` is accepted too, so a link written either way works.
 */
import {
  emailSchema,
  MIN_PASSWORD_LENGTH,
  setPasswordRequestSchema,
  setPasswordTokenSchema,
  type VerifySetPasswordTokenResponse,
} from '@wend/shared';
import { Button, buttonVariants, FormField, Input, Spinner } from '@wend/ui';
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import { useAuth } from '../auth/auth-context';
import { AuthLayout } from '../components/AuthLayout';
import { FormAlert } from '../components/FormAlert';
import {
  completeSetPassword,
  requestSetPasswordLink,
  verifySetPasswordToken,
} from '../lib/api/auth';
import { apiFieldErrors, errorMessage, isApiError, ROOT_FIELD } from '../lib/api/errors';
import { validate, withoutError, type FormErrors } from '../lib/form';

const PASSWORD_RULE = `At least ${MIN_PASSWORD_LENGTH} characters, with an upper case letter, a lower case letter and a number.`;

/**
 * `POST /auth/set-password/request` takes only an email, and `@wend/shared` has
 * no contract for a body that small. Composed from the shared `emailSchema` —
 * exactly as the API's own controller does — rather than re-declaring the rule,
 * so normalisation (trim + lower-case) matches on both sides.
 */
const requestLinkSchema = z.object({ email: emailSchema });

type TokenState =
  | { kind: 'absent' }
  | { kind: 'verifying' }
  | { kind: 'valid'; token: string; email: string; expiresAt: string | undefined }
  | { kind: 'expired' }
  | { kind: 'unavailable'; message: string; requestId: string | undefined };

function formatExpiry(value: string | undefined): string | null {
  if (value === undefined) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleString();
}

export function SetPasswordPage() {
  const params = useParams();
  const [searchParams] = useSearchParams();

  // Either link shape resolves to the same token.
  const rawToken = params.token ?? searchParams.get('token') ?? '';
  const parsedToken = setPasswordTokenSchema.safeParse(rawToken);
  const token = parsedToken.success ? parsedToken.data : null;

  const [tokenState, setTokenState] = useState<TokenState>(
    token === null ? { kind: 'absent' } : { kind: 'verifying' },
  );

  useEffect(() => {
    if (token === null) {
      setTokenState({ kind: 'absent' });
      return;
    }

    const controller = new AbortController();
    setTokenState({ kind: 'verifying' });

    verifySetPasswordToken(token, controller.signal)
      .then((result: VerifySetPasswordTokenResponse) => {
        if (controller.signal.aborted) return;
        setTokenState(
          result.valid && result.email !== undefined
            ? { kind: 'valid', token, email: result.email, expiresAt: result.expiresAt }
            : { kind: 'expired' },
        );
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        // A token that could not be *checked* is not the same as a token that
        // was checked and rejected, and saying "expired" here would be a lie.
        setTokenState({
          kind: 'unavailable',
          message: errorMessage(error),
          requestId: isApiError(error) ? error.requestId : undefined,
        });
      });

    return () => {
      controller.abort();
    };
  }, [token]);

  if (tokenState.kind === 'verifying') {
    return (
      <AuthLayout title="Checking your link" description="One moment.">
        <div className="flex items-center gap-3 text-[0.9rem] text-muted">
          <Spinner size="md" label={null} />
          <span role="status">Verifying the invitation link…</span>
        </div>
      </AuthLayout>
    );
  }

  if (tokenState.kind === 'valid') {
    return (
      <ChoosePasswordForm
        token={tokenState.token}
        email={tokenState.email}
        expiresAt={tokenState.expiresAt}
      />
    );
  }

  if (tokenState.kind === 'unavailable') {
    return (
      <AuthLayout
        title="We could not check that link"
        description="Nothing has been changed on your account."
        footer={
          <p>
            <Link to="/login" className="text-brand-b underline underline-offset-4 hover:text-text">
              Back to sign in
            </Link>
          </p>
        }
      >
        <FormAlert tone="error" requestId={tokenState.requestId}>
          {tokenState.message}
        </FormAlert>
      </AuthLayout>
    );
  }

  return <RequestLinkPanel reason={tokenState.kind} />;
}

/* -------------------------------------------------------------------------- */
/* Choosing the password                                                       */
/* -------------------------------------------------------------------------- */

function ChoosePasswordForm({
  token,
  email,
  expiresAt,
}: {
  token: string;
  email: string;
  expiresAt: string | undefined;
}) {
  const { signIn } = useAuth();
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});
  const [requestId, setRequestId] = useState<string | undefined>(undefined);
  const [pending, setPending] = useState(false);
  /** Set when the password was saved but the follow-up sign-in did not land. */
  const [savedNotSignedIn, setSavedNotSignedIn] = useState(false);

  const expiry = formatExpiry(expiresAt);

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (pending) return;

    setRequestId(undefined);

    const result = validate(setPasswordRequestSchema, { token, password });
    const failures: FormErrors = result.ok ? {} : { ...result.errors };

    if (password !== confirmation) {
      failures.confirmation = 'The two passwords do not match';
    }
    // The token came from the link, so a complaint about it is not something a
    // person can fix in this form — surface it as a link problem instead.
    if (failures.token !== undefined) {
      failures[ROOT_FIELD] = 'This link is invalid. Ask for a new one.';
      delete failures.token;
    }

    if (Object.keys(failures).length > 0) {
      setErrors(failures);
      return;
    }

    setErrors({});
    setPending(true);

    let confirmedEmail: string;
    try {
      const response = await completeSetPassword({ token, password });
      confirmedEmail = response.email;
    } catch (error) {
      setErrors({ ...apiFieldErrors(error), [ROOT_FIELD]: errorMessage(error) });
      setRequestId(isApiError(error) ? error.requestId : undefined);
      setPending(false);
      return;
    }

    // The password is set either way from here on. Signing in is a convenience,
    // so a failure must not read as "that did not work".
    try {
      await signIn({ email: confirmedEmail, password });
      navigate('/', { replace: true });
    } catch {
      setSavedNotSignedIn(true);
      setPending(false);
    }
  }

  if (savedNotSignedIn) {
    return (
      <AuthLayout title="Your password is set" description="You can sign in with it now.">
        <div className="flex flex-col gap-5">
          <FormAlert tone="success">
            We saved your new password but could not start a session automatically. Signing in
            directly will work.
          </FormAlert>
          <Link to="/login" className={buttonVariants({ fullWidth: true })}>
            Go to sign in
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Choose a password"
      description={
        <>
          You are setting up <span className="text-text">{email}</span>. Wend never emails a
          password — you pick it here and only you know it.
        </>
      }
      footer={expiry === null ? undefined : <p>This link stops working at {expiry}.</p>}
    >
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-5">
        {errors[ROOT_FIELD] !== undefined && (
          <FormAlert tone="error" requestId={requestId}>
            {errors[ROOT_FIELD]}
          </FormAlert>
        )}

        {/* Hidden, but present: password managers save the pair, not the password
            alone, and without it they file the entry under no account at all. */}
        <input type="hidden" name="username" autoComplete="username" value={email} readOnly />

        <FormField label="New password" description={PASSWORD_RULE} error={errors.password} required>
          <Input
            type="password"
            name="password"
            autoComplete="new-password"
            autoFocus
            required
            value={password}
            disabled={pending}
            onChange={(event) => {
              setPassword(event.target.value);
              setErrors((current) => withoutError(current, 'password'));
            }}
          />
        </FormField>

        <FormField label="Confirm password" error={errors.confirmation} required>
          <Input
            type="password"
            name="confirmation"
            autoComplete="new-password"
            required
            value={confirmation}
            disabled={pending}
            onChange={(event) => {
              setConfirmation(event.target.value);
              setErrors((current) => withoutError(current, 'confirmation'));
            }}
          />
        </FormField>

        <Button type="submit" loading={pending} fullWidth>
          {pending ? 'Setting your password' : 'Set password and continue'}
        </Button>
      </form>
    </AuthLayout>
  );
}

/* -------------------------------------------------------------------------- */
/* Asking for a fresh link                                                     */
/* -------------------------------------------------------------------------- */

function RequestLinkPanel({ reason }: { reason: 'absent' | 'expired' }) {
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});
  const [requestId, setRequestId] = useState<string | undefined>(undefined);
  const [pending, setPending] = useState(false);
  const [sent, setSent] = useState(false);

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (pending) return;

    setRequestId(undefined);

    const result = validate(requestLinkSchema, { email });
    if (!result.ok) {
      setErrors(result.errors);
      return;
    }

    setErrors({});
    setPending(true);

    try {
      await requestSetPasswordLink(result.data.email);
      setSent(true);
    } catch (error) {
      setErrors({ [ROOT_FIELD]: errorMessage(error) });
      setRequestId(isApiError(error) ? error.requestId : undefined);
    } finally {
      setPending(false);
    }
  }

  const title = reason === 'expired' ? 'That link has expired' : 'Get a set-password link';
  const description =
    reason === 'expired'
      ? 'Set-password links last 24 hours and work once. Ask for a new one below.'
      : 'Onboarding links arrive by email and last 24 hours. Enter your work address to get one.';

  return (
    <AuthLayout
      title={title}
      description={description}
      footer={
        <p>
          Already have a password?{' '}
          <Link to="/login" className="text-brand-b underline underline-offset-4 hover:text-text">
            Sign in
          </Link>
        </p>
      }
    >
      {sent ? (
        <FormAlert tone="success">
          If that address belongs to a Wend account, a fresh link is on its way. It expires in 24
          hours.
        </FormAlert>
      ) : (
        <form onSubmit={onSubmit} noValidate className="flex flex-col gap-5">
          {errors[ROOT_FIELD] !== undefined && (
            <FormAlert tone="error" requestId={requestId}>
              {errors[ROOT_FIELD]}
            </FormAlert>
          )}

          <FormField label="Email" error={errors.email} required>
            <Input
              type="email"
              name="email"
              autoComplete="email"
              autoFocus
              required
              placeholder="you@company.com"
              value={email}
              disabled={pending}
              onChange={(event) => {
                setEmail(event.target.value);
                setErrors((current) => withoutError(current, 'email'));
              }}
            />
          </FormField>

          <Button type="submit" loading={pending} fullWidth>
            {pending ? 'Sending' : 'Send me a link'}
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
